/**
 * AdminFirmQueues — three moderation queues at `/admin/firm-portal`.
 *
 *   Tab 1 · Claims       — approve/reject `pending_admin` firm_claims
 *   Tab 2 · Profile edits — approve/reject `pending` firm_edits
 *   Tab 3 · Band submissions — approve/reject `pending` firm_band_submissions
 *
 * Only platform admins (via require_admin on the backend) see this.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, ExternalLink, Clock, CalendarDays, MapPin, Video } from "lucide-react";

export default function AdminFirmQueues() {
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState([]);
  const [edits, setEdits] = useState([]);
  const [bands, setBands] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const refetch = async () => {
    setLoading(true);
    const [c, e, b, ev] = await Promise.allSettled([
      api.get("/admin/firm-claims"),
      api.get("/admin/firm-edits"),
      api.get("/admin/firm-band-submissions"),
      api.get("/admin/firm-events"),
    ]);
    if (c.status === "fulfilled") setClaims(c.value.data.claims || []);
    if (e.status === "fulfilled") setEdits(e.value.data.edits || []);
    if (b.status === "fulfilled") setBands(b.value.data.submissions || []);
    if (ev.status === "fulfilled") setEvents(ev.value.data.events || []);
    setLoading(false);
  };

  useEffect(() => { refetch(); }, []);

  if (loading) return <main className="max-w-5xl mx-auto px-6 py-8 text-stone-500">Loading…</main>;

  return (
    <main className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="admin-firm-queues-root">
      <header>
        <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
          Firm portal moderation
        </div>
        <h1 className="text-3xl font-editorial">Firm queues</h1>
        <p className="text-sm text-stone-600 mt-1">
          Approve or reject firm claims, profile edits, and salary band submissions.
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="admin-firm-queues-tabs">
          <TabsTrigger value="claims" data-testid="admin-firm-tab-claims">
            Claims {claims.length > 0 && <span className="ml-1.5 text-[10px] font-mono bg-amber-100 text-amber-900 rounded px-1.5">{claims.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="edits" data-testid="admin-firm-tab-edits">
            Profile edits {edits.length > 0 && <span className="ml-1.5 text-[10px] font-mono bg-amber-100 text-amber-900 rounded px-1.5">{edits.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="bands" data-testid="admin-firm-tab-bands">
            Band submissions {bands.length > 0 && <span className="ml-1.5 text-[10px] font-mono bg-amber-100 text-amber-900 rounded px-1.5">{bands.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="admin-firm-tab-events">
            Events {events.length > 0 && <span className="ml-1.5 text-[10px] font-mono bg-amber-100 text-amber-900 rounded px-1.5">{events.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="claims" className="pt-6">
          {claims.length === 0 ? (
            <EmptyRow label="No pending claims — the queue is clear." />
          ) : (
            <ul className="space-y-3">
              {claims.map((c) => <ClaimRow key={c.id} claim={c} onDone={refetch} />)}
            </ul>
          )}
        </TabsContent>
        <TabsContent value="edits" className="pt-6">
          {edits.length === 0 ? (
            <EmptyRow label="No pending edits — the queue is clear." />
          ) : (
            <ul className="space-y-3">
              {edits.map((e) => <EditRow key={e.id} edit={e} onDone={refetch} />)}
            </ul>
          )}
        </TabsContent>
        <TabsContent value="bands" className="pt-6">
          {bands.length === 0 ? (
            <EmptyRow label="No pending band submissions — the queue is clear." />
          ) : (
            <ul className="space-y-3">
              {bands.map((b) => <BandRow key={b.id} band={b} onDone={refetch} />)}
            </ul>
          )}
        </TabsContent>
        <TabsContent value="events" className="pt-6">
          {events.length === 0 ? (
            <EmptyRow label="No pending events — the queue is clear." />
          ) : (
            <ul className="space-y-3">
              {events.map((ev) => <EventRow key={ev.id} event={ev} onDone={refetch} />)}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </main>
  );
}

function EmptyRow({ label }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 p-6 text-sm text-stone-500 text-center" data-testid="admin-firm-queue-empty">
      {label}
    </div>
  );
}

function ClaimRow({ claim, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/firm-claims/${claim.id}/${action}`, { admin_note: note });
      toast.success(`Claim ${action}d.`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decide");
    }
    setBusy(false);
  };
  return (
    <li className="rounded-lg border border-stone-200 bg-white p-4 space-y-2" data-testid={`admin-firm-claim-row-${claim.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{claim.firm_name}</span>
        <span className="text-stone-500 font-mono text-xs">/{claim.firm_slug}</span>
        <span className="text-[10px] font-underground-sc tracking-wider rounded-full border border-stone-200 bg-stone-50 text-stone-600 px-2 py-0.5">
          {claim.status}
        </span>
        <span className="ml-auto text-xs text-stone-400 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {new Date(claim.created_at).toLocaleString("en-GB")}
        </span>
      </div>
      <div className="text-sm text-stone-700">
        <strong>{claim.user_name || claim.user_email}</strong> — <span className="text-stone-500">{claim.job_title || "no title"}</span>
      </div>
      <div className="text-xs text-stone-500 space-y-0.5">
        <div>Work email: {claim.work_email}</div>
        {claim.linkedin_url && (
          <div>LinkedIn: <a href={claim.linkedin_url} target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline">{claim.linkedin_url}</a></div>
        )}
      </div>
      <Textarea
        placeholder="Optional admin note (visible in audit log)"
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className="text-xs"
        data-testid={`admin-firm-claim-note-${claim.id}`}
      />
      <div className="flex gap-2">
        <Button data-testid={`admin-firm-claim-approve-${claim.id}`} onClick={() => decide("approve")} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
        <Button data-testid={`admin-firm-claim-reject-${claim.id}`} onClick={() => decide("reject")} disabled={busy} size="sm" variant="outline">
          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      </div>
    </li>
  );
}

function EditRow({ edit, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/firm-edits/${edit.id}/${action}`, { admin_note: note });
      toast.success(`Edit ${action}d.`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decide");
    }
    setBusy(false);
  };
  return (
    <li className="rounded-lg border border-stone-200 bg-white p-4 space-y-2" data-testid={`admin-firm-edit-row-${edit.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{edit.firm_name}</span>
        <span className="text-stone-500 font-mono text-xs">/{edit.firm_slug}</span>
        <span className="text-[10px] font-underground-sc tracking-wider rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] text-[#4C1D95] px-2 py-0.5">
          {edit.field}
        </span>
        <span className="ml-auto text-xs text-stone-400 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {new Date(edit.created_at).toLocaleString("en-GB")}
        </span>
      </div>
      <div className="text-xs text-stone-500">Editor: {edit.editor_email}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-400 mb-1">Before</div>
          <pre className="rounded bg-stone-50 border border-stone-200 p-2 text-xs whitespace-pre-wrap overflow-x-auto" data-testid={`admin-firm-edit-before-${edit.id}`}>
            {typeof edit.value_before === "string" ? edit.value_before : JSON.stringify(edit.value_before, null, 2)}
          </pre>
        </div>
        <div>
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-400 mb-1">After (proposed)</div>
          <pre className="rounded bg-amber-50 border border-amber-200 p-2 text-xs whitespace-pre-wrap overflow-x-auto" data-testid={`admin-firm-edit-after-${edit.id}`}>
            {typeof edit.value_after === "string" ? edit.value_after : JSON.stringify(edit.value_after, null, 2)}
          </pre>
        </div>
      </div>
      <Textarea placeholder="Optional admin note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="text-xs" />
      <div className="flex gap-2">
        <Button data-testid={`admin-firm-edit-approve-${edit.id}`} onClick={() => decide("approve")} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
        <Button data-testid={`admin-firm-edit-reject-${edit.id}`} onClick={() => decide("reject")} disabled={busy} size="sm" variant="outline">
          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      </div>
    </li>
  );
}

function BandRow({ band, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/firm-band-submissions/${band.id}/${action}`, { admin_note: note });
      toast.success(`Band ${action}d.`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decide");
    }
    setBusy(false);
  };
  return (
    <li className="rounded-lg border border-stone-200 bg-white p-4 space-y-2" data-testid={`admin-firm-band-row-${band.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{band.firm_name}</span>
        <span className="text-stone-500 font-mono text-xs">/{band.firm_slug}</span>
        <span className="ml-auto text-xs text-stone-400 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {new Date(band.created_at).toLocaleString("en-GB")}
        </span>
      </div>
      <div className="text-sm text-stone-900">
        <strong>{band.role}</strong> · {band.location} · {band.currency} {band.base.toLocaleString()}
        {band.total_comp && <> · TC {band.total_comp.toLocaleString()}</>}
        {band.bonus && <> · Bonus {band.bonus.toLocaleString()}</>}
      </div>
      <div className="text-xs text-stone-500">Submitted by {band.submitter_email}</div>
      <div className="text-xs text-stone-700">
        Source URL: <a href={band.source_url} target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline inline-flex items-center gap-1">
          {band.source_url} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="text-xs text-stone-700 bg-stone-50 border border-stone-200 rounded p-2 whitespace-pre-wrap">
        {band.justification}
      </div>
      <Textarea placeholder="Optional admin note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="text-xs" />
      <div className="flex gap-2">
        <Button data-testid={`admin-firm-band-approve-${band.id}`} onClick={() => decide("approve")} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
        <Button data-testid={`admin-firm-band-reject-${band.id}`} onClick={() => decide("reject")} disabled={busy} size="sm" variant="outline">
          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      </div>
    </li>
  );
}


function EventRow({ event, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/firm-events/${event.id}/${action}`, { admin_note: note });
      toast.success(`Event ${action}d.`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decide");
    }
    setBusy(false);
  };
  const fmtDT = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  return (
    <li className="rounded-lg border border-stone-200 bg-white p-4 space-y-2" data-testid={`admin-firm-event-row-${event.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{event.firm_name}</span>
        <span className="text-stone-500 font-mono text-xs">/{event.firm_slug}</span>
        <span className="text-[10px] font-underground-sc tracking-wider rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] text-[#4C1D95] px-2 py-0.5">
          {event.event_type}
        </span>
        <span className="ml-auto text-xs text-stone-400 flex items-center gap-1">
          <Clock className="h-3 w-3" /> submitted {new Date(event.created_at).toLocaleString("en-GB")}
        </span>
      </div>
      <div className="text-sm font-semibold text-stone-900">{event.title}</div>
      <div className="text-xs text-stone-600 flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {fmtDT(event.starts_at)} → {fmtDT(event.ends_at)}</span>
        <span className="inline-flex items-center gap-1">
          {event.is_virtual ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
          {event.is_virtual ? "Virtual" : event.location}
        </span>
      </div>
      <div className="text-xs text-stone-700 bg-stone-50 border border-stone-200 rounded p-2 whitespace-pre-wrap">
        {event.description}
      </div>
      <div className="text-xs text-stone-500">
        RSVP: <a href={event.rsvp_url} target="_blank" rel="noreferrer" className="text-[#4C1D95] hover:underline inline-flex items-center gap-1">
          {event.rsvp_url} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      {(event.target_year_groups?.length > 0 || event.target_subjects?.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {(event.target_year_groups || []).map((yg) => <span key={yg} className="text-[10px] font-underground-sc tracking-wider rounded-full border border-stone-200 bg-stone-50 text-stone-600 px-2 py-0.5">{yg}</span>)}
          {(event.target_subjects || []).map((s) => <span key={s} className="text-[10px] font-underground-sc tracking-wider rounded-full border border-stone-200 bg-stone-50 text-stone-600 px-2 py-0.5">{s}</span>)}
        </div>
      )}
      <Textarea placeholder="Optional admin note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="text-xs" />
      <div className="flex gap-2">
        <Button data-testid={`admin-firm-event-approve-${event.id}`} onClick={() => decide("approve")} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
        <Button data-testid={`admin-firm-event-reject-${event.id}`} onClick={() => decide("reject")} disabled={busy} size="sm" variant="outline">
          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      </div>
    </li>
  );
}
